/**
 * Search API Endpoints
 * Hybrid search, semantic search, and agent-safe queries
 */

import { http } from '@/lib/http'
import { env } from '@/lib/env'
import { wsPath } from '@/lib/api/workspace-context'

/**
 * Object in search result
 */
export interface SearchResultObject {
  id: string
  type: string
  title: string
  createdAt: string
  updatedAt: string
  status?: string
  visibility?: string
  version?: number
  [key: string]: unknown // attributes are spread into object
}

/**
 * Search result item
 */
export interface SearchResultItem {
  object: SearchResultObject
  textScore?: number
  vectorScore?: number
  conversationContext?: string[]
}

/**
 * Search response
 */
export interface SearchResponse {
  results: SearchResultItem[]
}

export interface HybridSearchOptions {
  types?: string[]
  conversationIds?: string[]
  limit?: number // Note: limit is not currently supported by API
}

export interface SemanticSearchOptions {
  types?: string[]
  conversationIds?: string[]
  limit?: number
  threshold?: number // similarity threshold (0-1)
}

export interface AgentSearchOptions {
  toolSession?: string
}

/**
 * Hybrid search (text + vector)
 * Best for user-facing search boxes
 */
export async function hybridSearch(
  authToken: string | null,
  query: string,
  options?: HybridSearchOptions
) {
  const searchParams: Record<string, string> = { query }
  // Note: limit is not supported by the /search endpoint per OpenAPI spec
  if (options?.types) searchParams.types = options.types.join(',')
  if (options?.conversationIds) searchParams.conversationIds = options.conversationIds.join(',')

  const response = await http.get<SearchResponse>(wsPath('/search'), {
    authToken,
    searchParams,
  })

  return response.data
}

/**
 * Semantic search (vector only)
 * Best for "find similar" queries
 */
export async function semanticSearch(
  authToken: string | null,
  query: string,
  options?: SemanticSearchOptions
) {
  const searchParams: Record<string, string> = { query }
  if (options?.limit) searchParams.limit = options.limit.toString()
  if (options?.types) searchParams.types = options.types.join(',')
  if (options?.conversationIds) searchParams.conversationIds = options.conversationIds.join(',')
  if (options?.threshold) searchParams.threshold = options.threshold.toString()

  const response = await http.get<SearchResponse>(wsPath('/semantic-search'), {
    authToken,
    searchParams,
  })

  return response.data
}

/**
 * Agent-safe search
 * Used by agent tools for querying user data
 */
export async function agentSearch(
  authToken: string | null,
  query: string,
  options?: AgentSearchOptions
) {
  const headers: Record<string, string> = {}
  if (env.agentKey) headers['X-Agent-Key'] = env.agentKey
  if (options?.toolSession) headers['X-Tool-Session'] = options.toolSession

  const response = await http.post<SearchResponse>(
    wsPath('/agent/tools/query'),
    { query },
    {
      authToken,
      headers,
    }
  )

  return response.data
}
